import { Link } from "react-router-dom";
import { ArrowRight, Award } from "lucide-react";
import { useLang } from "@/context/LangContext";

const ISO_LOGO = "https://customer-assets.emergentagent.com/job_secure-certified/artifacts/ha193hnt_iso.png";
const IAF_LOGO = "https://customer-assets.emergentagent.com/job_secure-certified/artifacts/18doz6dk_iad.svg.png";

const accreditations = [
  {
    name: "IAS (International Accreditation Service)",
    desc: "GACA delivers certification services through partners accredited by IAS, a member of IAF (International Accreditation Forum) and signatory of the MLA (Multilateral Recognition Arrangement).",
    standards: ["ISO 9001", "ISO 14001", "ISO 45001", "ISO 27001", "ISO 22301", "ISO 37001"],
    region: "Global",
  },
  {
    name: "IAF (International Accreditation Forum)",
    desc: "Our accredited certification body partners are recognized under the IAF Multilateral Recognition Arrangement, ensuring global acceptance of certifications issued.",
    standards: ["Management Systems", "Product Certification", "Personnel Certification"],
    region: "Worldwide",
  },
];

const certificationScopes = [
  { standard: "ISO 9001:2015", name: "Quality Management Systems", status: "Active" },
  { standard: "ISO 14001:2015", name: "Environmental Management Systems", status: "Active" },
  { standard: "ISO 45001:2018", name: "Occupational Health & Safety", status: "Active" },
  { standard: "ISO 27001:2022", name: "Information Security Management", status: "Active" },
  { standard: "ISO 22301:2019", name: "Business Continuity Management", status: "Active" },
  { standard: "ISO 27701:2019", name: "Privacy Information Management", status: "Active" },
  { standard: "ISO 37001:2016", name: "Anti-Bribery Management Systems", status: "Active" },
  { standard: "NIST CSF", name: "Cybersecurity Framework", status: "Advisory" },
  { standard: "SOC 2", name: "Service Organization Controls", status: "Readiness" },
];

function HeroSection({ ap }) {
  return (
    <section className="bg-[#0B2545] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">{ap.badge || "Accreditations"}</p>
        <h1 data-testid="accreditations-page-heading" className="text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-black text-white leading-[1.1] mb-6" style={{ fontFamily: "Chivo, sans-serif" }}>
          {ap.title || "Accredited Partnerships"}
        </h1>
        <p className="text-lg text-white/70 max-w-2xl leading-relaxed">
          {ap.subtitle || "GACA International delivers certification services in partnership with internationally accredited certification bodies."}
        </p>
      </div>
    </section>
  );
}

function LogoBar() {
  return (
    <section className="bg-[#F7F9FC] border-b border-[#0B2545]/10 py-10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-center gap-12 md:gap-20">
        <img src={ISO_LOGO} alt="ISO" className="h-14 md:h-16 w-auto" data-testid="accreditations-iso-logo" />
        <img src={IAF_LOGO} alt="IAF" className="h-14 md:h-16 w-auto" data-testid="accreditations-iaf-logo" />
      </div>
    </section>
  );
}

function PartnersSection() {
  return (
    <section className="py-20 lg:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">Our Partners</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>
            Accreditation Bodies
          </h2>
        </div>
        <div className="space-y-0 border border-[#0B2545]/10">
          {accreditations.map((acc, i) => (
            <div key={acc.name} data-testid={`accreditation-${i}`} className={`p-8 lg:p-12 border-b border-[#0B2545]/10 last:border-b-0 ${i % 2 === 1 ? 'bg-[#F5F7FA]' : 'bg-white'}`}>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div className="lg:col-span-2">
                  <div className="flex items-center gap-3 mb-4">
                    <Award size={24} className="text-[#13A8BD]" />
                    <h3 className="text-xl font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>{acc.name}</h3>
                  </div>
                  <p className="text-base text-slate-600 leading-relaxed mb-4">{acc.desc}</p>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Region: {acc.region}</span>
                </div>
                <div>
                  <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">Scope</h4>
                  <div className="flex flex-wrap gap-2">
                    {acc.standards.map((s) => (
                      <span key={s} className="text-xs px-3 py-1.5 border border-[#0B2545]/15 text-[#0B2545]">{s}</span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ScopesSection() {
  return (
    <section className="py-20 lg:py-28 bg-[#F5F7FA] border-t border-[#0B2545]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="mb-14">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#13A8BD] mb-4">Scope</p>
          <h2 className="text-3xl md:text-4xl tracking-tight font-bold text-[#0B2545]" style={{ fontFamily: "Chivo, sans-serif" }}>
            Certification Standards
          </h2>
        </div>
        <div className="border border-[#0B2545]/10 bg-white">
          <div className="grid grid-cols-3 gap-0 border-b border-[#0B2545]/10 bg-[#0B2545] text-white">
            <div className="p-4 text-xs font-bold uppercase tracking-wider">Standard</div>
            <div className="p-4 text-xs font-bold uppercase tracking-wider">Description</div>
            <div className="p-4 text-xs font-bold uppercase tracking-wider text-right">Status</div>
          </div>
          {certificationScopes.map((scope) => (
            <div key={scope.standard} className="grid grid-cols-3 gap-0 border-b border-[#0B2545]/10 last:border-b-0 hover:bg-[#F5F7FA] transition-colors">
              <div className="p-4 text-sm font-bold text-[#0B2545]">{scope.standard}</div>
              <div className="p-4 text-sm text-slate-600">{scope.name}</div>
              <div className="p-4 text-right">
                <span className={`text-xs font-bold px-3 py-1 ${scope.status === 'Active' ? 'bg-[#13A8BD]/10 text-[#13A8BD]' : 'bg-slate-100 text-slate-500'}`}>
                  {scope.status}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function LegalNotice() {
  return (
    <section className="py-16 bg-white border-t border-[#0B2545]/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="border-l-4 border-[#13A8BD] pl-8">
          <p className="text-sm text-slate-600 leading-relaxed">
            <strong>Important Notice:</strong> GACA International delivers certification services
            in partnership with internationally accredited certification bodies. All certifications
            are issued through our accredited partners. GACA International itself is not an accredited
            certification body. The IAF-MLA mark on certificates is used under the authority of our
            accredited partner bodies.
          </p>
        </div>
        <div className="mt-10 text-center">
          <Link to="/contact" data-testid="accreditations-cta" className="inline-flex items-center gap-2 bg-[#13A8BD] text-white px-8 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0e8a9c] transition-colors">
            Request Certification <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </section>
  );
}

export default function AccreditationsPage() {
  const { t } = useLang();
  const ap = t?.accreditations_page || {};
  return (
    <div className="page-transition pt-20" data-testid="accreditations-page">
      <HeroSection ap={ap} />
      <LogoBar />
      <PartnersSection />
      <ScopesSection />
      <LegalNotice />
    </div>
  );
}
